import React, { useEffect, useRef, useState } from 'react';
import parse from 'html-react-parser';
import { useAnnouncement } from '../contexts/AnnouncementContext';
import styles from '../styles/AnnouncementBanner.module.css';
import FaIcon from './FaIcon';

/**
 * AnnouncementBanner - Displays a global announcement at the top of the header.
 * 
 * Renders nothing if there's no active announcement.
 * Supports custom text and background colors from the announcement data.
 * Users can dismiss the banner for the current session.
 */
const AnnouncementBanner = () => {
  const { announcement, dismissAnnouncement } = useAnnouncement();
  const messageRef = useRef(null);
  const contentRef = useRef(null);
  const [shouldScroll, setShouldScroll] = useState(false);

  useEffect(() => {
    if (!announcement || !messageRef.current || !contentRef.current) {
      setShouldScroll(false);
      return undefined;
    }

    let frameId;
    const evaluateOverflow = () => {
      frameId = requestAnimationFrame(() => {
        const containerWidth = messageRef.current?.offsetWidth || 0;
        const contentWidth = contentRef.current?.scrollWidth || 0;
        const availableWidth = Math.max(containerWidth - 60, 0);
        setShouldScroll(contentWidth > availableWidth);
      });
    };

    evaluateOverflow();

    const resizeObserver = new ResizeObserver(() => {
      evaluateOverflow();
    });

    resizeObserver.observe(messageRef.current);
    resizeObserver.observe(contentRef.current);

    return () => {
      resizeObserver.disconnect();
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [announcement]);

  if (!announcement) {
    return null;
  }

  const bannerStyle = {
    backgroundColor: announcement.background_color || '#000000',
    color: announcement.text_color || '#FFFFFF',
  };

  return (
    <div className={styles.banner} style={bannerStyle}>
      <div
        ref={messageRef}
        className={`${styles.message} ${shouldScroll ? styles.messageScrolling : ''}`}
      >
        <span
          ref={contentRef}
          className={`${styles.messageContent} ${shouldScroll ? styles.scroll : ''}`}
        >
          {parse(announcement.message || '')}
        </span>
      </div>
      <button
        className={styles.dismissButton}
        onClick={dismissAnnouncement}
        aria-label="Chiudi annuncio"
        style={{ color: announcement.text_color || '#FFFFFF' }}
      >
        <FaIcon className="fas fa-times"  />
      </button>
    </div>
  );
};

export default AnnouncementBanner;
